import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Card,
    CardContent,
    Grid,
    Button,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import Icon from "@/theme/vectors/bundle";
import { useTranslation } from "@/hooks/useTranslation";
import { useSqlQuery } from "@/hooks/database/useSqlQuery";
import { useAiPrompt } from "@/hooks/database/useAiPrompt";
import { formatTimestamp, isUnixTimestamp } from "@/utils/database/formatters";

const SqlQueryPanel = () => {
    const { t } = useTranslation();
    
    // SQL query state and hooks
    const { 
        query, 
        setQuery, 
        result, 
        loading, 
        error, 
        executeQuery 
    } = useSqlQuery();
    
    // AI prompt state and hooks
    const {
        prompt,
        setPrompt,
        promptLoading,
        promptError,
        processPrompt
    } = useAiPrompt(setQuery);
    
    // Prompt modal state
    const [promptModalOpen, setPromptModalOpen] = useState(false);
    
    const handleQueryChange = (event) => {
        setQuery(event.target.value);
    };
    
    const handlePromptChange = (event) => {
        setPrompt(event.target.value);
    };
    
    const handleOpenPromptModal = () => {
        setPromptModalOpen(true);
    };
    
    const handleClosePromptModal = () => {
        setPromptModalOpen(false);
    };
    
    const handleProcessPrompt = () => {
        setPromptModalOpen(false);
        processPrompt();
    };
    
    const renderResultTable = (data) => {
        if (!data || data.length === 0) return <Typography>{t('database.query.noData')}</Typography>;
        
        // Get all unique keys from all objects in the data array
        const allKeys = [...new Set(data.flatMap(item => Object.keys(item)))];
        
        return (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {allKeys.map(key => (
                                <TableCell key={key}>{key}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {allKeys.map(key => (
                                    <TableCell key={key}>
                                        {isUnixTimestamp(row[key]) ? (
                                            <div title={formatTimestamp(row[key])}>
                                                {row[key]}
                                            </div>
                                        ) : (
                                            row[key] !== undefined ? String(row[key]) : null
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <>
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                label={t('database.query.inputLabel')}
                                multiline
                                rows={5}
                                fullWidth
                                variant="outlined"
                                value={query}
                                onChange={handleQueryChange}
                                placeholder={t('database.query.placeholder')}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button 
                                    variant="contained" 
                                    color="primary"
                                    sx={{ mt: 2 }}
                                    onClick={executeQuery}
                                    disabled={!query.trim() || loading}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name="send" size={20} />}
                                >
                                    {t('database.query.execute')}
                                </Button>
                                
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ mt: 2 }}
                                    onClick={handleOpenPromptModal}
                                    disabled={promptLoading}
                                    startIcon={promptLoading ? <CircularProgress size={20} color="inherit" /> : <Icon name="sparkle3" size={20} />}
                                >
                                    {t('database.query.prompt')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">{error.message}</Typography>
                    {error.errors && error.errors.query && (
                        <ul>
                            {error.errors.query.map((err, index) => (
                                <li key={index}>{err}</li>
                            ))}
                        </ul>
                    )}
                </Alert>
            )}
            
            {result && result.isSuccess && (
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                {t('database.query.results')}: {result.message}
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                onClick={executeQuery} 
                                disabled={loading || !query.trim()}
                                startIcon={<Icon name="refresh2" size={20} />}
                                size="small"
                            >
                                {t('database.query.runAgain')}
                            </Button>
                        </Box>
                        
                        {result.data && result.data.length > 0 ? (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {result.data.length} {t('database.query.recordsReturned', { count: result.data.length })}
                                </Typography>
                                {renderResultTable(result.data)}
                            </>
                        ) : (
                            <Typography>{t('database.query.noData')}</Typography>
                        )}
                        
                        {result.metadata && (
                            <Box sx={{ mt: 2, color: 'text.secondary' }}>
                                <Typography variant="caption">
                                    {t('database.query.operation')}: {result.metadata.operation} • 
                                    {t('database.query.timestamp')}: {new Date(result.metadata.timestamp).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: false
                                    }).replace(',', '')}
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}
            
            {/* Prompt Modal */}
            <Dialog 
                open={promptModalOpen} 
                onClose={handleClosePromptModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Icon name="sparkle3" size={20} />
                        <span>{t('database.prompt.title')}</span>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('database.prompt.inputLabel')}
                        multiline
                        rows={5}
                        fullWidth
                        variant="outlined"
                        value={prompt}
                        onChange={handlePromptChange}
                        placeholder={t('database.prompt.placeholder')}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePromptModal} color="primary" startIcon={<Icon name="close" size={16} />}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleProcessPrompt} color="primary" variant="contained" startIcon={<Icon name="check" size={16} />} disabled={!prompt.trim()}>
                        {t('database.prompt.process')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SqlQueryPanel;